/**
 * Shared display helpers for the Form Workspace (trec-svc forms).
 *
 * Form-type labels and the status → StatusPill tone mapping live here so the
 * list (Forms.tsx) and detail (FormDetail.tsx) screens stay in sync.
 */

import type {
  FieldSource,
  FormStatus,
  FormType,
} from "@re-platform/api-client";
import type { StatusTone } from "@re-platform/ui";

/** TREC form types the staff can open — mirrors the server FormType literal. */
export const FORM_TYPES: { value: FormType; label: string }[] = [
  {
    value: "residential_contract_1_to_4_family",
    label: "Residential Contract (1–4 Family)",
  },
  { value: "buyer_representation", label: "Buyer Representation" },
  {
    value: "exclusive_right_to_sell",
    label: "Listing Agreement — Exclusive Right to Sell",
  },
  {
    value: "exclusive_right_to_lease",
    label: "Listing Agreement — Exclusive Right to Lease",
  },
  { value: "sellers_disclosure", label: "Seller's Disclosure" },
  { value: "lease_application", label: "Lease Application" },
  { value: "tenant_application", label: "Tenant Application" },
  { value: "addendum", label: "Addendum" },
  { value: "amendment", label: "Amendment" },
];

const FORM_TYPE_LABELS = new Map(FORM_TYPES.map((t) => [t.value, t.label]));

export function formTypeLabel(type: FormType | string): string {
  return FORM_TYPE_LABELS.get(type as FormType) ?? type;
}

export function statusLabel(status: FormStatus): string {
  switch (status) {
    case "DRAFT":
      return "Draft";
    case "PREFILLED":
      return "Prefilled";
    case "SENT_FOR_SIGNATURE":
      return "Sent for signature";
    case "SIGNED":
      return "Signed";
    default:
      return status;
  }
}

export function statusTone(status: FormStatus): StatusTone {
  switch (status) {
    case "SIGNED":
      return "success";
    case "SENT_FOR_SIGNATURE":
      return "info";
    case "PREFILLED":
      return "warning";
    default:
      return "neutral";
  }
}

/** Fields are only editable while the form is still DRAFT or PREFILLED. */
export function isEditable(status: FormStatus): boolean {
  return status === "DRAFT" || status === "PREFILLED";
}

export function sourceLabel(source: FieldSource): string {
  switch (source) {
    case "auto_extracted":
      return "Auto-extracted";
    case "agent_edit":
      return "Agent edit";
    case "client_input":
      return "Client input";
    case "manual":
      return "Manual";
    default:
      return source;
  }
}

export function sourceTone(source: FieldSource): StatusTone {
  switch (source) {
    case "auto_extracted":
      return "info";
    case "agent_edit":
      return "warning";
    case "client_input":
      return "success";
    default:
      return "neutral";
  }
}

/** Humanize a canonical field_name (e.g. buyer_full_name → "Buyer full name"). */
export function fieldLabel(name: string): string {
  const spaced = name.replace(/_/g, " ").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
